import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, X } from 'lucide-react';

const CameraCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    
    // Prevent ALL scrolling - save original styles
    const originalStyles = {
      htmlOverflow: document.documentElement.style.overflow,
      bodyOverflow: document.body.style.overflow,
      htmlPosition: document.documentElement.style.position,
      bodyPosition: document.body.style.position,
      bodyWidth: document.body.style.width,
      bodyHeight: document.body.style.height
    };
    
    // Lock everything
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.position = 'fixed';
    document.documentElement.style.width = '100%';
    document.documentElement.style.height = '100%';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    const startCamera = async () => {
      try {
        console.log('Requesting camera access...');
        
        // Request camera with constraints
        const constraints = {
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        };
        
        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        console.log('Camera access granted:', mediaStream);
        
        if (!mounted) {
          mediaStream.getTracks().forEach(track => track.stop());
          return;
        }
        
        setStream(mediaStream);
        
        // Set video source
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          
          // Wait for video to load
          videoRef.current.onloadedmetadata = async () => {
            console.log('Video metadata loaded');
            try {
              await videoRef.current.play();
              console.log('Video playing');
              if (mounted) {
                setIsLoading(false);
              }
            } catch (playError) {
              console.error('Play error:', playError);
              if (mounted) {
                setError('Failed to start video playback');
                setIsLoading(false);
              }
            }
          };
          
          videoRef.current.onerror = (e) => {
            console.error('Video error:', e);
            if (mounted) {
              setError('Video playback error');
              setIsLoading(false);
            }
          };
        }
      } catch (err) {
        console.error('Camera error:', err);
        if (mounted) {
          let errorMessage = 'Could not access camera. ';
          if (err.name === 'NotAllowedError') {
            errorMessage += 'Please allow camera access in your browser settings.';
          } else if (err.name === 'NotFoundError') {
            errorMessage += 'No camera found on this device.';
          } else {
            errorMessage += err.message || 'Unknown error';
          }
          setError(errorMessage);
          setIsLoading(false);
        }
      }
    };

    startCamera();

    return () => {
      mounted = false;
      // Restore all original styles
      document.documentElement.style.overflow = originalStyles.htmlOverflow;
      document.documentElement.style.position = originalStyles.htmlPosition;
      document.documentElement.style.width = '';
      document.documentElement.style.height = '';
      document.body.style.overflow = originalStyles.bodyOverflow;
      document.body.style.position = originalStyles.bodyPosition;
      document.body.style.width = originalStyles.bodyWidth;
      document.body.style.height = originalStyles.bodyHeight;
      
      if (stream) {
        stream.getTracks().forEach(track => {
          console.log('Stopping track:', track.label);
          track.stop();
        });
      }
    };
  }, []);

  const capturePhoto = () => {
    if (!videoRef.current || isLoading) {
      console.log('Cannot capture - video not ready');
      return;
    }

    try {
      console.log('Capturing photo...');
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      
      if (canvas.width === 0 || canvas.height === 0) {
        console.error('Invalid video dimensions');
        return;
      }
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      console.log('Photo captured, size:', imageData.length);
      
      onCapture(imageData);
      handleClose();
    } catch (err) {
      console.error('Capture error:', err);
      alert('Failed to capture photo');
    }
  };

  const handleClose = () => {
    console.log('Closing camera');
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    onClose();
  };

  return (
    <div 
      style={{ 
        position: 'fixed', 
        top: '0px', 
        left: '0px',
        bottom: '0px',
        right: '0px',
        width: '100%',
        height: '100%',
        backgroundColor: 'black',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        margin: '0px',
        padding: '0px',
        overflow: 'hidden',
        boxSizing: 'border-box',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {/* Header */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px',
          backgroundColor: 'rgba(15, 23, 42, 0.8)',
          zIndex: 100000,
          boxSizing: 'border-box'
        }}
      >
        <h3 style={{ color: 'white', fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
          Take Photo
        </h3>
        <button 
          onClick={handleClose}
          style={{
            color: 'white',
            padding: '8px',
            background: 'transparent',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          aria-label="Close camera"
        >
          <X className="w-6 h-6" />
        </button>
      </div>
      
      {/* Video Container - Full Screen */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'black',
          zIndex: 99998,
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        {error ? (
          <div style={{
            textAlign: 'center',
            color: 'white',
            padding: '24px',
            maxWidth: '400px',
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            borderRadius: '8px'
          }}>
            <p style={{ marginBottom: '16px', fontSize: '18px' }}>{error}</p>
            <Button 
              onClick={handleClose}
              className="bg-white text-black hover:bg-slate-200"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                display: isLoading ? 'none' : 'block',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0
              }}
            />
            
            {isLoading && (
              <div style={{
                textAlign: 'center',
                color: 'white',
                zIndex: 99999
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  border: '4px solid white',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  margin: '0 auto 16px',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ fontSize: '20px', fontWeight: 'bold' }}>Starting camera...</p>
                <p style={{ fontSize: '14px', color: '#cbd5e1', marginTop: '8px' }}>
                  Please allow camera access if prompted
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Capture Button - Fixed at Bottom */}
      {!error && !isLoading && (
        <div 
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            padding: '32px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(15, 23, 42, 0.8)',
            zIndex: 100000,
            boxSizing: 'border-box'
          }}
        >
          <button
            onClick={capturePhoto}
            data-testid="capture-photo-btn"
            style={{
              width: '80px',
              height: '80px',
              background: 'white',
              borderRadius: '50%',
              border: '4px solid white',
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0
            }}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
            onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <div 
              style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Camera className="w-8 h-8 text-white" />
            </div>
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Prevent ALL scrollbars when camera is open */
        html {
          overflow: hidden !important;
          overflow-x: hidden !important;
          overflow-y: hidden !important;
          position: fixed !important;
          width: 100% !important;
          height: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        body {
          overflow: hidden !important;
          overflow-x: hidden !important;
          overflow-y: hidden !important;
          position: fixed !important;
          width: 100% !important;
          height: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        * {
          -webkit-overflow-scrolling: auto !important;
        }
        
        /* Hide scrollbars completely */
        ::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
        }
        
        * {
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
        }
      `}</style>
    </div>
  );
};

export default CameraCapture;
